<template>
    <div>
        <!-- Preview Modal -->
        <PreviewModal
            :isVisible="isPreviewModalVisible"
            :streamName="recordToPreview.name"
            @copy="handlePreviewCopy"
            @close="handlePreviewClose"
        />
        <!-- Notification Container -->
        <div class="notification-container">
            <div
                v-for="(notification, index) in notifications"
                :key="index"
                :class="['notification', notification.type]"
            >
                {{ notification.message }}
            </div>
        </div>
        <div v-if="!isAuthenticated" class="login-container">
            <h1>HLS Manager Login</h1>
            <input type="password" v-model="password" placeholder="Password" @keydown.enter="login"/>
            <button @click="login">Login</button>
            <p v-if="loginError" class="error-message">{{ loginError }}</p>
        </div>
        <div v-else>
            <h1>HLS Manager</h1>
            <button class="logout-button" @click="logout">Logout</button>
            <p v-if="logoutMessage" class="success-message">{{ logoutMessage }}</p>

            <div class="form-container">
                <h2>{{ editing ? 'Edit Stream' : 'Add Stream' }}</h2>
                <input v-model="form.source" placeholder="Source URL" />
                <input v-model="form.name" placeholder="Stream Name" />
                <label>Output Format</label><select v-model="form.codec">
                    <option value="MP3">MP3</option>
                    <option value="AAC">AAC</option>
                    <option value="AAC HE V1">AAC HE V1</option>
                    <option value="AAC HE V2">AAC HE V2</option>
                </select>
                <button @click="saveRecord">{{ editing ? 'Update' : 'Add' }}</button>
            </div>
            <table>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Source</th>
                        <th>Codec</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody v-if="records.length > 0">
                    <tr v-for="record in records" :key="record.id">
                        <td data-label="ID">{{ record.id }}</td>
                        <td data-label="Source">{{ record.source }}</td>
                        <td data-label="Codec">{{ record.codec }}</td>
                        <td data-label="Name">{{ record.name }}</td>
                        <td data-label="Status" ><p :class="{
                            'status-failed': record.status === '-1',
                            'status-stopped': record.status === '0',
                            'status-startstop': record.status === '1',
                            'status-running': record.status === '2',
                          }">{{ record.status }}</p></td>
                        <td data-label="Actions" class="actions">
                            <button @click="editRecord(record)">Edit</button>
                            <button @click="deleteRecord(record.id)">Delete</button>
                            <button @click="startService(record.id)">Start</button>
                            <button @click="stopService(record.id)">Stop</button>
                            <button v-if="record.status == 2" @click="showPreviewModal(record)">Preview</button>
                            <!--<a :href="'/streams/'+record.name+'.m3u8'">Listen</a>-->
                        </td>
                    </tr>
                 </tbody>
                <tbody v-else>
                    <tr>
                        <td colspan="6" class="no-records-message">No streams configured</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import PreviewModal from './PreviewModal.vue';

export default {
    components: {
      PreviewModal,
    },
    mounted() {
      document.title = 'HLS Manager'
      setInterval(this.fetchRecords, 3000);
    },
    data() {
        return {
            isAuthenticated: false,
            password: '',
            records: [],
            form: { id: null, source: '', codec: 'MP3', name: '' },
            editing: false,
            notifications: [], // Array to hold multiple notifications
            timer: '',
            isPreviewModalVisible: false,
            recordToPreview: '',
        };
    },
    async created() {
        await this.checkAuth();
        if (this.isAuthenticated) {
            await this.fetchRecords();
        }
        //this.timer = setInterval(await this.fetchRecords(),3000);
    },
    methods: {
        showPreviewModal(record) {
          this.recordToPreview = record;
          this.isPreviewModalVisible = true;
        },
        handlePreviewCopy() {
          this.isPreviewModalVisible = false;
        },
        handlePreviewClose() {
          this.isPreviewModalVisible = false;
        },
        showNotification(message, type = 'success') {
            const id = Date.now(); // Use a timestamp as a unique ID
            const notification = { id, message, type };
            this.notifications.push(notification); // Add the new notification
            setTimeout(() => {
                this.notifications = this.notifications.filter(n => n.id !== id); // Remove the notification by ID
            }, 3000); // Hide notification after 3 seconds
        },
        async checkAuth() {
            const response = await fetch('/api/', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ action: 'checkauth' })
            });
            const data = await response.json();
            this.isAuthenticated = data.success || false;
        },
        async login() {
            const response = await fetch('/api/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'login', password: this.password })
            });
            const data = await response.json();
            if (data.success) {
                this.isAuthenticated = true;
                this.showNotification(data.message || 'Login success.');
                await this.fetchRecords();
            } else {
                this.showNotification(data.message || 'Unknown error.', 'error');
            }
        },
        async logout() {
            const response = await fetch('/api/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'logout' })
            });
            const data = await response.json();
            if (data.success) {
                this.isAuthenticated = false;
                this.showNotification(data.message || 'Logout success.');
            }
        },
        async fetchRecords() {
            if (this.isAuthenticated) {
                const response = await fetch('/api/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'fetch' })
                });
                const data = await response.json();
                if (data.success) {
                    this.records = data.records;
                } else {
                    this.showNotification(data.message || 'Unknown error.', 'error');
                }
            }
        },
        editRecord(record) {
            this.form = { ...record };
            this.editing = true; // Set the editing flag to true
        },
        async saveRecord() {
            const response = await fetch('/api/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'save',
                    id: this.form.id,
                    source: this.form.source,
                    codec: this.form.codec,
                    name: this.form.name
                })
            });
            const data = await response.json();
            if (data.success) {
                this.form = { id: null, source: '', codec: 'MP3', name: '' };
                this.editing = false;
                await this.fetchRecords();
                this.showNotification(data.message || 'Success.');
            } else {
                this.showNotification(data.message || 'Unknown error.', 'error');
            }
        },
        async deleteRecord(id) {
            // Show confirmation dialog
            const isConfirmed = confirm('Are you sure you want to delete stream ID ' + id + '?');
            if (!isConfirmed) return; // Exit if the user cancels
            const response = await fetch('/api/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'delete', id })
            });
            const data = await response.json();
            if (data.success) {
                await this.fetchRecords();
                this.showNotification(data.message || 'Success.');
            } else {
                this.showNotification(data.message || 'Unknown error.', 'error');
            }
        },
        async startService(id) {
            const response = await fetch('/api/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, action: 'start' })
            });
            const data = await response.json();
            if (data.success) {
                await this.fetchRecords();
                this.showNotification(data.message || 'Success.');
            } else {
                this.showNotification(data.message || 'Unknown error.', 'error');
            }
        },
        async stopService(id) {
            const response = await fetch('/api/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, action: 'stop' })
            });
            const data = await response.json();
            if (data.success) {
                await this.fetchRecords();
                this.showNotification(data.message || 'Success.');
            } else {
                this.showNotification(data.message || 'Unknown error.', 'error');
            }
        }
    }
};
</script>
